import { useMutation, useQuery } from '@apollo/client/react';
import {
  OPTICLOCK_ADD_ENTRY,
  OPTICLOCK_ADMIN_DELETE_ENTRY,
  OPTICLOCK_ADMIN_UPDATE_ENTRY,
  OPTICLOCK_AGENT_ENTRIES,
  OPTICLOCK_DELETE_ENTRY,
  OPTICLOCK_MY_ENTRIES,
  OPTICLOCK_PROJECTS,
  OPTICLOCK_UPDATE_ENTRY,
} from '@staff-app/graphql-client';
import type {
  OpticlockEntry,
  OpticlockEntryInput,
  OpticlockEntryPatchInput,
  OpticlockMyMonth,
  OpticlockProject,
} from '@staff-app/shared-types';

/**
 * OptiClock entry-page data.
 *
 * Split across three hook files (this, useOpticlockDashboard, useOpticlockProjects) so the
 * entry page never imports the root-only mutations, and so each page's `vi.mock` of its hook
 * module stays small.
 */

/** The live project list for the picker. Archived categories are excluded server-side. */
export function useOpticlockProjectPicker() {
  return useQuery<{ opticlockProjects: OpticlockProject[] }>(OPTICLOCK_PROJECTS, {
    variables: { includeArchived: false },
    fetchPolicy: 'cache-and-network',
  });
}

/**
 * One month of entries for the agent being viewed.
 *
 * `agentEmail` is undefined for the normal case — the caller's own month, where the SERVER
 * reads the identity from the token and the client never sends an email. It is set only when
 * root has switched the page's agent selector, which routes to the root-only query instead.
 */
export function useOpticlockMonth(month: string, agentEmail?: string) {
  const mine = useQuery<{ opticlockMyEntries: OpticlockMyMonth }>(OPTICLOCK_MY_ENTRIES, {
    variables: { month },
    fetchPolicy: 'cache-and-network',
    skip: !!agentEmail,
  });
  const other = useQuery<{ opticlockAgentEntries: OpticlockMyMonth }>(OPTICLOCK_AGENT_ENTRIES, {
    variables: { agentEmail, month },
    fetchPolicy: 'cache-and-network',
    skip: !agentEmail,
  });
  const active = agentEmail ? other : mine;
  const data = agentEmail
    ? (other.data as { opticlockAgentEntries?: OpticlockMyMonth } | undefined)
        ?.opticlockAgentEntries
    : (mine.data as { opticlockMyEntries?: OpticlockMyMonth } | undefined)?.opticlockMyEntries;
  return { month: data, loading: active.loading, error: active.error, refetch: active.refetch };
}

/** Both month queries are refetched by name, so the footer total can never lag the rows. */
const REFETCH = ['opticlockMyEntries', 'opticlockAgentEntries'];

export function useAddOpticlockEntry() {
  return useMutation<{ opticlockAddEntry: OpticlockEntry }, { input: OpticlockEntryInput }>(
    OPTICLOCK_ADD_ENTRY,
    { refetchQueries: REFETCH },
  );
}

export function useUpdateOpticlockEntry() {
  return useMutation<
    { opticlockUpdateEntry: OpticlockEntry },
    { id: string; patch: OpticlockEntryPatchInput }
  >(OPTICLOCK_UPDATE_ENTRY, { refetchQueries: REFETCH });
}

export function useDeleteOpticlockEntry() {
  return useMutation<{ opticlockDeleteEntry: boolean }, { id: string }>(OPTICLOCK_DELETE_ENTRY, {
    refetchQueries: REFETCH,
  });
}

/**
 * Root editing an entry it does not own. `agentEmail` is the ROW OWNER's email — the server
 * matches on it and refuses a mismatch; it is never root's own address and never reassigns
 * ownership. Also refetches the dashboard, whose totals just moved.
 */
export function useAdminUpdateOpticlockEntry() {
  return useMutation<
    { opticlockAdminUpdateEntry: OpticlockEntry },
    { id: string; agentEmail: string; patch: OpticlockEntryPatchInput }
  >(OPTICLOCK_ADMIN_UPDATE_ENTRY, {
    refetchQueries: [...REFETCH, 'opticlockAgentMonthTotals'],
  });
}

export function useAdminDeleteOpticlockEntry() {
  return useMutation<
    { opticlockAdminDeleteEntry: boolean },
    { id: string; agentEmail: string }
  >(OPTICLOCK_ADMIN_DELETE_ENTRY, {
    refetchQueries: [...REFETCH, 'opticlockAgentMonthTotals'],
  });
}
